import { doHandshake, HandshakeSession } from "./handshake";

export interface CryptoSession {
  sid: string;
  sessionKey: Uint8Array;
  txCounter: number;
  expireAt: number;
}

const STORAGE_KEY = "bettimax_crypto_session";

let cachedSession: CryptoSession | null = null;
let handshakeInFlight: Promise<CryptoSession> | null = null;
let counterLock: Promise<void> = Promise.resolve();

function saveSession(s: CryptoSession) {
  cachedSession = s;
  sessionStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      sid: s.sid,
      sessionKey: Array.from(s.sessionKey),
      txCounter: s.txCounter,
      expireAt: s.expireAt,
    }),
  );
}

function loadSession(): CryptoSession | null {
  if (cachedSession) return cachedSession;
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const j = JSON.parse(raw);
    cachedSession = {
      sid: j.sid,
      sessionKey: new Uint8Array(j.sessionKey),
      txCounter: j.txCounter,
      expireAt: j.expireAt,
    };
    return cachedSession;
  } catch {
    return null;
  }
}

export function invalidateSession() {
  cachedSession = null;
  handshakeInFlight = null;
  sessionStorage.removeItem(STORAGE_KEY);
}

async function createSession(apiOrigin: string): Promise<CryptoSession> {
  const hs: HandshakeSession = await doHandshake(apiOrigin);
  const s: CryptoSession = {
    sid: hs.sid,
    sessionKey: hs.sessionKey,
    txCounter: 0,
    expireAt: hs.expireAt,
  };
  saveSession(s);
  return s;
}

export async function ensureSession(apiOrigin: string): Promise<CryptoSession> {
  const existing = loadSession();
  if (existing && Date.now() < existing.expireAt) return existing;

  if (handshakeInFlight) return handshakeInFlight;
  handshakeInFlight = (async () => {
    invalidateSession();
    const next = await createSession(apiOrigin);
    handshakeInFlight = null;
    return next;
  })();
  return handshakeInFlight;
}

function withCounterLock<T>(fn: () => Promise<T>): Promise<T> {
  const run = counterLock.then(fn, fn);
  counterLock = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

export async function reserveRequestCounter(
  apiOrigin: string,
): Promise<{ sid: string; sessionKey: Uint8Array; counter: number }> {
  return withCounterLock(async () => {
    const s = await ensureSession(apiOrigin);
    const nextCounter = s.txCounter + 1;
    const updated: CryptoSession = { ...s, txCounter: nextCounter };
    saveSession(updated);
    return { sid: updated.sid, sessionKey: updated.sessionKey, counter: nextCounter };
  });
}
